import { MapPin, Phone, Star, Clock, Users, Shield } from "lucide-react";
import { CityData } from "@/utils/cityData";
import { Button } from "@/components/ui/button";
import HeroSection from "@/components/layout/HeroSection";
import { useImagePreload } from "@/hooks/useImagePreload";
import frankfurtHero from "@/assets/stadt/frankfurt-hero.jpg";
import koelnHero from "@/assets/stadt/koeln-hero.jpg";
import hamburgHero from "@/assets/stadt/hamburg-hero.jpg";
import muenchenHero from "@/assets/stadt/muenchen-hero.jpg";
import berlinHero from "@/assets/stadt/berlin-hero.jpg";

interface CityHeroProps {
  city: CityData;
}

const cityImages: Record<string, string> = {
  "Frankfurt am Main": frankfurtHero,
  "Köln": koelnHero,
  "Hamburg": hamburgHero,
  "München": muenchenHero,
  "Berlin": berlinHero,
};

const CityHero = ({ city }: CityHeroProps) => {
  const heroImage = cityImages[city.name] || frankfurtHero;
  
  // Preload hero image for better LCP
  useImagePreload(heroImage);
  
  return (
    <HeroSection
      image={heroImage}
      alt={`Skyline von ${city.name} - Lokale Cyberversicherung für Arztpraxen mit persönlicher Beratung vor Ort`}
      title={`Cyberversicherung für Arztpraxen in ${city.name} – Schützen Sie Ihre Patientendaten vor Hackern`}
      gradientPreset="none"
      imageOpacity={40}
      overlayColor="background"
      className="py-20 sm:py-24 lg:py-32 px-4"
    >
      <div className="container mx-auto max-w-4xl text-center relative z-10">
        {/* Icon-Kreis wie Homepage */}
        <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-accent/10 rounded-full mb-6">
          <MapPin className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-accent" />
        </div>
        
        {/* Hauptüberschrift */}
        <h1 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6">
          Cyberversicherung für Arztpraxen in {city.name} – Lokaler Schutz, Expertenberatung
        </h1>
        
        {/* Subheadline */}
        <div className="space-y-6 mb-6">
          <p className="text-base sm:text-lg lg:text-xl text-foreground max-w-2xl mx-auto font-semibold">
            Schützen Sie Ihre Patientendaten vor Hackern – Komplett-Schutz ab 29€/Monat
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/20 border border-accent/40 rounded-full">
            <Shield className="w-4 h-4 text-accent" />
            <span className="text-accent font-semibold text-sm">
              {city.stats.practiceCount}+ Arztpraxen in {city.name}
            </span>
          </div>
        </div>
      </div>
    </HeroSection>
  );
};

export default CityHero;